import React, { useMemo, useRef, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const CAMERAS_API = "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";
const MAX_MOTION_REGIONS = 8;
const MIN_REGION_SIZE = 0.02;

export type MotionRegion = {
  id: string;
  name: string;
  enabled: boolean;
  sensitivity: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type CameraMotionConfig = {
  version: 1;
  enabled: boolean;
  sensitivity: number;
  regions: MotionRegion[];
  source: "portal" | "camera" | "unknown";
  syncStatus: "NOT_SYNCED" | "PENDING" | "SYNCED" | "ERROR";
  cameraCapabilities?: {
    regionSupport?: boolean;
    sensitivitySupport?: boolean;
    sensitivityValues?: number[];
    sensitivityMin?: number;
    sensitivityMax?: number;
    sensitivityStep?: number;
    disarmSupport?: boolean;
    polygonSupport?: boolean;
    singleSensitivitySupport?: boolean;
    maxRegions?: number;
  };
  lastReadAt?: string;
  lastSyncedAt?: string;
  syncError?: string;
};

type Camera = {
  cameraId: string;
  name?: string;
  thumbnailUrl?: string;
  motionEventsEnabled?: boolean;
  motionEventsStatus?: string;
  motionEventsError?: string;
  motionEventsLastSeenAt?: string;
  motionConfig?: Partial<CameraMotionConfig>;
  [key: string]: unknown;
};

type Props = {
  camera: Camera;
  onUpdateCamera: (camera: any) => void;
};

type Point = { x: number; y: number };
type DraftRegion = Pick<MotionRegion, "x" | "y" | "width" | "height">;

const buttonStyle: React.CSSProperties = {
  minHeight: 38,
  padding: "8px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#fff",
  color: "#334155",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
};

function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function round(value: number) {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function normalizeSensitivity(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(clamp(parsed, 0, 100)) : 50;
}

function normalizeRegion(value: unknown, index: number, fallbackSensitivity: number): MotionRegion | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Partial<MotionRegion>;
  const x = clamp(Number(candidate.x));
  const y = clamp(Number(candidate.y));
  const width = clamp(Number(candidate.width), 0, 1 - x);
  const height = clamp(Number(candidate.height), 0, 1 - y);
  if (![x, y, width, height].every(Number.isFinite)) return null;
  if (width < MIN_REGION_SIZE || height < MIN_REGION_SIZE) return null;
  return {
    id: String(candidate.id || `motion-region-${index + 1}`),
    name: String(candidate.name || `Motion region ${index + 1}`),
    enabled: candidate.enabled !== false,
    sensitivity: normalizeSensitivity(candidate.sensitivity ?? fallbackSensitivity),
    x: round(x),
    y: round(y),
    width: round(width),
    height: round(height),
  };
}

export function normalizeMotionConfig(value: unknown, motionEventsEnabled = false): CameraMotionConfig {
  const candidate = value && typeof value === "object" && !Array.isArray(value)
    ? (value as Partial<CameraMotionConfig>)
    : {};
  const sensitivity = normalizeSensitivity(candidate.sensitivity);
  const regions = (Array.isArray(candidate.regions) ? candidate.regions : [])
    .map((region, index) => normalizeRegion(region, index, sensitivity))
    .filter((region): region is MotionRegion => Boolean(region))
    .slice(0, MAX_MOTION_REGIONS);
  return {
    version: 1,
    enabled: typeof candidate.enabled === "boolean" ? candidate.enabled : motionEventsEnabled,
    sensitivity,
    regions,
    source: ["portal", "camera", "unknown"].includes(String(candidate.source))
      ? (candidate.source as CameraMotionConfig["source"])
      : "unknown",
    syncStatus: ["NOT_SYNCED", "PENDING", "SYNCED", "ERROR"].includes(String(candidate.syncStatus))
      ? (candidate.syncStatus as CameraMotionConfig["syncStatus"])
      : "NOT_SYNCED",
    cameraCapabilities: candidate.cameraCapabilities,
    lastReadAt: candidate.lastReadAt,
    lastSyncedAt: candidate.lastSyncedAt,
    syncError: candidate.syncError,
  };
}

async function getIdToken() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Your session expired. Sign in again.");
  return token;
}

async function readResponse(response: Response) {
  const text = await response.text();
  try {
    return { text, json: text ? JSON.parse(text) : null };
  } catch {
    return { text, json: null };
  }
}

function newRegionId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? `motion-${crypto.randomUUID()}`
    : `motion-${Date.now()}`;
}

function nextRegionName(regions: MotionRegion[]) {
  let index = 1;
  const names = new Set(regions.map((region) => region.name));
  while (names.has(`Motion region ${index}`)) index += 1;
  return `Motion region ${index}`;
}

function statusTone(status: CameraMotionConfig["syncStatus"]) {
  if (status === "SYNCED") return { color: "#166534", background: "#f0fdf4", border: "#bbf7d0" };
  if (status === "ERROR") return { color: "#b91c1c", background: "#fef2f2", border: "#fecaca" };
  if (status === "PENDING") return { color: "#92400e", background: "#fffbeb", border: "#fde68a" };
  return { color: "#475569", background: "#f8fafc", border: "#cbd5e1" };
}

export default function MotionSettingsTab({ camera, onUpdateCamera }: Props) {
  const initial = useMemo(
    () => normalizeMotionConfig(camera.motionConfig, camera.motionEventsEnabled === true),
    [camera.cameraId, camera.motionConfig, camera.motionEventsEnabled],
  );
  const [config, setConfig] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [drawing, setDrawing] = useState(false);
  const [draft, setDraft] = useState<DraftRegion | null>(null);
  const startRef = useRef<Point | null>(null);
  const [aspectRatio, setAspectRatio] = useState("16 / 9");
  const [saving, setSaving] = useState(false);
  const [syncingAction, setSyncingAction] = useState<"" | "read" | "apply">("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);

  const isDirty = JSON.stringify(config) !== JSON.stringify(saved);
  const activeRegions = config.regions.filter((region) => region.enabled);
  const capabilities = config.cameraCapabilities;
  const reportedSensitivityValues = capabilities?.sensitivityValues;
  const sensitivityValues = Array.isArray(reportedSensitivityValues)
    ? [...new Set(reportedSensitivityValues.map(Number).filter(Number.isFinite))].sort((a, b) => a - b)
    : [];
  const sensitivityMin = Number.isFinite(Number(capabilities?.sensitivityMin))
    ? Number(capabilities?.sensitivityMin)
    : 0;
  const sensitivityMax = Number.isFinite(Number(capabilities?.sensitivityMax))
    ? Number(capabilities?.sensitivityMax)
    : 100;
  const sensitivityStep = Number.isFinite(Number(capabilities?.sensitivityStep)) && Number(capabilities?.sensitivityStep) > 0
    ? Number(capabilities?.sensitivityStep)
    : 1;
  const syncTone = statusTone(config.syncStatus);

  function setGlobalSensitivity(value: number) {
    setConfig((current) => ({
      ...current,
      sensitivity: value,
      regions: current.regions.map((region) => ({ ...region, sensitivity: value })),
      source: "portal",
      syncStatus: "NOT_SYNCED",
    }));
  }

  function point(event: React.PointerEvent<HTMLDivElement>): Point | null {
    const box = event.currentTarget.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    return {
      x: clamp((event.clientX - box.left) / box.width),
      y: clamp((event.clientY - box.top) / box.height),
    };
  }

  function rectangle(start: Point, end: Point): DraftRegion {
    return {
      x: round(Math.min(start.x, end.x)),
      y: round(Math.min(start.y, end.y)),
      width: round(Math.abs(start.x - end.x)),
      height: round(Math.abs(start.y - end.y)),
    };
  }

  function begin(event: React.PointerEvent<HTMLDivElement>) {
    if (!drawing || saving || !config.enabled || config.regions.length >= MAX_MOTION_REGIONS) return;
    const current = point(event);
    if (!current) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    startRef.current = current;
    setDraft({ x: current.x, y: current.y, width: 0, height: 0 });
  }

  function move(event: React.PointerEvent<HTMLDivElement>) {
    if (!startRef.current) return;
    const current = point(event);
    if (current) setDraft(rectangle(startRef.current, current));
  }

  function finish(event: React.PointerEvent<HTMLDivElement>) {
    if (!startRef.current) return;
    const current = point(event);
    const start = startRef.current;
    startRef.current = null;
    setDraft(null);
    if (!current) return;
    const area = rectangle(start, current);
    if (area.width < MIN_REGION_SIZE || area.height < MIN_REGION_SIZE) {
      setError(true);
      setMessage("The motion region is too small. Draw a larger area.");
      return;
    }
    const region: MotionRegion = {
      id: newRegionId(),
      name: nextRegionName(config.regions),
      enabled: true,
      sensitivity: config.sensitivity,
      ...area,
    };
    setConfig((currentConfig) => ({
      ...currentConfig,
      regions: [...currentConfig.regions, region],
      source: "portal",
      syncStatus: "NOT_SYNCED",
    }));
    setDrawing(false);
    setError(false);
    setMessage(`${region.name} added. Save the desired settings, then the ONVIF sync service can apply them to the camera.`);
  }

  function updateRegion(regionId: string, changes: Partial<MotionRegion>) {
    setConfig((current) => ({
      ...current,
      regions: current.regions.map((region) => region.id === regionId ? { ...region, ...changes } : region),
      source: "portal",
      syncStatus: "NOT_SYNCED",
    }));
  }

  function deleteRegion(regionId: string) {
    setConfig((current) => ({
      ...current,
      regions: current.regions.filter((region) => region.id !== regionId),
      source: "portal",
      syncStatus: "NOT_SYNCED",
    }));
  }

  async function syncCameraMotion(action: "read" | "apply") {
    if (saving || syncingAction) return;
    setSyncingAction(action);
    setError(false);
    setMessage(
      action === "read"
        ? "Reading motion settings from the camera..."
        : "Applying and verifying motion settings on the camera...",
    );
    try {
      const token = await getIdToken();
      let workingConfig = config;

      if (action === "apply" && isDirty) {
        const desiredConfig: CameraMotionConfig = {
          ...config,
          source: "portal",
          syncStatus: "PENDING",
          syncError: undefined,
        };
        const saveResponse = await fetch(
          CAMERAS_API + "/" + encodeURIComponent(camera.cameraId),
          {
            method: "PATCH",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
            body: JSON.stringify({ motionConfig: desiredConfig }),
          },
        );
        const savedResponse = await readResponse(saveResponse);
        if (!saveResponse.ok) {
          throw new Error(
            savedResponse.json?.error ||
              savedResponse.json?.message ||
              savedResponse.text ||
              saveResponse.statusText,
          );
        }
        workingConfig = normalizeMotionConfig(
          savedResponse.json?.camera?.motionConfig || desiredConfig,
          desiredConfig.enabled,
        );
        setConfig(workingConfig);
        setSaved(workingConfig);
      }

      const response = await fetch(
        CAMERAS_API + "/" + encodeURIComponent(camera.cameraId) + "/motion-settings/" + action,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        },
      );
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(json?.error || json?.message || text || response.statusText);
      }
      const synchronized = normalizeMotionConfig(
        json?.motionConfig,
        workingConfig.enabled,
      );
      setConfig(synchronized);
      setSaved(synchronized);
      onUpdateCamera({ ...camera, motionConfig: synchronized });
      setMessage(
        action === "read"
          ? "Camera motion settings loaded successfully."
          : "Motion settings applied and verified on the camera.",
      );
    } catch (cause) {
      setError(true);
      const actionLabel = action === "read" ? "Unable to read" : "Unable to apply";
      const detail = cause instanceof Error ? cause.message : "Unexpected error";
      setMessage(actionLabel + " camera motion settings: " + detail);
    } finally {
      setSyncingAction("");
    }
  }

  async function saveDesiredSettings() {
    if (!isDirty || saving) return;
    setSaving(true);
    setError(false);
    setMessage("Saving desired motion settings...");
    try {
      const token = await getIdToken();
      const desiredConfig: CameraMotionConfig = {
        ...config,
        source: "portal",
        syncStatus: "PENDING",
        syncError: undefined,
      };
      const response = await fetch(`${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ motionConfig: desiredConfig }),
      });
      const { text, json } = await readResponse(response);
      if (!response.ok) throw new Error(json?.error || json?.message || text || response.statusText);
      const persisted = normalizeMotionConfig(json?.camera?.motionConfig || desiredConfig, desiredConfig.enabled);
      setConfig(persisted);
      setSaved(persisted);
      onUpdateCamera({ ...camera, ...(json?.camera || {}), motionConfig: persisted });
      setMessage("Desired motion settings saved. Use Apply to camera when you are ready to synchronize them through ONVIF.");
    } catch (cause) {
      setError(true);
      setMessage(`Unable to save motion settings: ${cause instanceof Error ? cause.message : "Unexpected error"}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={{ display: "grid", gap: 18, padding: 22, border: "1px solid #e2e8f0", borderRadius: 16, background: "#fff" }}>
      <header style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "#7c3aed", fontSize: 11, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase" }}>Camera motion</div>
          <h3 style={{ margin: "5px 0 7px", color: "#0f172a" }}>Motion settings</h3>
          <p style={{ margin: 0, maxWidth: 780, color: "#64748b", lineHeight: 1.55 }}>
            Configure desired camera motion sensitivity and active regions. Camera-specific controls are enabled only after the ONVIF service reports support.
          </p>
        </div>
        <label style={{ ...buttonStyle, display: "inline-flex", alignItems: "center", gap: 9, background: "#f8fafc" }}>
          <input
            type="checkbox"
            role="switch"
            checked={config.enabled}
            disabled={saving}
            onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked, source: "portal", syncStatus: "NOT_SYNCED" }))}
          />
          Enable camera motion
        </label>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12 }}>
        <div style={{ padding: 13, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 900, textTransform: "uppercase" }}>Collector status</div>
          <strong style={{ display: "block", marginTop: 5, color: "#0f172a", fontSize: 13 }}>{camera.motionEventsStatus || (camera.motionEventsEnabled ? "ENABLED" : "DISABLED")}</strong>
          <div style={{ marginTop: 5, color: camera.motionEventsError ? "#b91c1c" : "#64748b", fontSize: 11 }}>{camera.motionEventsError || "No collector error reported."}</div>
        </div>
        <div style={{ padding: 13, border: `1px solid ${syncTone.border}`, borderRadius: 12, background: syncTone.background }}>
          <div style={{ color: syncTone.color, fontSize: 10, fontWeight: 900, textTransform: "uppercase" }}>Camera sync</div>
          <strong style={{ display: "block", marginTop: 5, color: syncTone.color, fontSize: 13 }}>{config.syncStatus.replaceAll("_", " ")}</strong>
          <div style={{ marginTop: 5, color: syncTone.color, fontSize: 11 }}>{config.syncError || "Awaiting camera-side ONVIF synchronization."}</div>
        </div>
        <div style={{ padding: 13, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}>
          <div style={{ color: "#64748b", fontSize: 10, fontWeight: 900, textTransform: "uppercase" }}>Motion regions</div>
          <strong style={{ display: "block", marginTop: 5, color: "#0f172a", fontSize: 13 }}>{activeRegions.length} active / {config.regions.length} total</strong>
          <div style={{ marginTop: 5, color: "#64748b", fontSize: 11 }}>Device limit: {capabilities?.maxRegions ?? "not read yet"}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(230px, .72fr) minmax(340px, 1.5fr)", gap: 18, opacity: config.enabled ? 1 : 0.58 }}>
        <aside style={{ display: "grid", gap: 14, alignContent: "start" }}>
          <label style={{ display: "grid", gap: 8, color: "#334155", fontSize: 12, fontWeight: 800 }}>
            Global sensitivity: {config.sensitivity}%
            {sensitivityValues.length > 0 ? (
              <select
                aria-label="Global motion sensitivity"
                value={config.sensitivity}
                disabled={!config.enabled || saving || capabilities?.sensitivitySupport === false}
                onChange={(event) => setGlobalSensitivity(Number(event.target.value))}
                style={{ minHeight: 38, padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 9, background: "#fff", font: "inherit" }}
              >
                {sensitivityValues.map((value) => (
                  <option key={value} value={value}>{value}%</option>
                ))}
              </select>
            ) : (
              <input
                type="range"
                min={sensitivityMin}
                max={sensitivityMax}
                step={sensitivityStep}
                value={config.sensitivity}
                disabled={!config.enabled || saving || capabilities?.sensitivitySupport === false}
                onChange={(event) => setGlobalSensitivity(Number(event.target.value))}
              />
            )}
          </label>
          <div style={{ color: "#64748b", fontSize: 11, lineHeight: 1.5 }}>
            {sensitivityValues.length > 0
              ? `This camera reports these supported sensitivity values: ${sensitivityValues.join(", ")}.`
              : `Available range: ${sensitivityMin}–${sensitivityMax}${sensitivityStep > 1 ? ` in steps of ${sensitivityStep}` : ""}. Higher values react to smaller changes.`}
            {capabilities?.singleSensitivitySupport === true ? " This camera uses one global sensitivity value for every motion region." : ""}
          </div>
          <button
            type="button"
            disabled={!config.enabled || saving || config.regions.length >= MAX_MOTION_REGIONS || capabilities?.regionSupport === false}
            onClick={() => setDrawing((current) => !current)}
            style={{ ...buttonStyle, borderColor: drawing ? "#fca5a5" : "#c4b5fd", color: drawing ? "#b91c1c" : "#6d28d9", background: drawing ? "#fef2f2" : "#f5f3ff" }}
          >
            {drawing ? "Cancel drawing" : "+ Draw motion region"}
          </button>
          <button
            type="button"
            disabled={saving || Boolean(syncingAction) || isDirty}
            onClick={() => void syncCameraMotion("read")}
            style={{
              ...buttonStyle,
              opacity: saving || syncingAction || isDirty ? 0.55 : 1,
            }}
            title={isDirty ? "Reset or save local changes before reading from the camera" : undefined}
          >
            {syncingAction === "read" ? "Reading..." : "Read settings from camera"}
          </button>
          <button
            type="button"
            disabled={saving || Boolean(syncingAction)}
            onClick={() => void syncCameraMotion("apply")}
            style={{
              ...buttonStyle,
              borderColor: "#7c3aed",
              color: "#fff",
              background: "#7c3aed",
              opacity: saving || syncingAction ? 0.55 : 1,
            }}
          >
            {syncingAction === "apply"
              ? "Applying..."
              : isDirty
                ? "Save & apply to camera"
                : "Apply to camera"}
          </button>
          <div style={{ padding: 12, border: "1px solid #ddd6fe", borderRadius: 10, color: "#5b21b6", background: "#faf5ff", fontSize: 11, lineHeight: 1.5 }}>
            Read discovers the camera's standard ONVIF MotionRegionDetector capabilities and rules. Apply writes the desired regions, reads them back, and marks the configuration synced only after verification.
          </div>
        </aside>

        <div
          data-testid="motion-mask-canvas"
          onPointerDown={begin}
          onPointerMove={move}
          onPointerUp={finish}
          style={{ position: "relative", width: "100%", aspectRatio, overflow: "hidden", border: `2px solid ${drawing ? "#7c3aed" : "#cbd5e1"}`, borderRadius: 14, background: "#e2e8f0", cursor: drawing ? "crosshair" : "default", userSelect: "none", touchAction: "none" }}
        >
          {camera.thumbnailUrl ? (
            <img
              src={camera.thumbnailUrl}
              alt={`${camera.name || "Camera"} motion preview`}
              draggable={false}
              onLoad={(event) => {
                const image = event.currentTarget;
                if (image.naturalWidth && image.naturalHeight) setAspectRatio(`${image.naturalWidth} / ${image.naturalHeight}`);
              }}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill", pointerEvents: "none" }}
            />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#64748b", fontSize: 12 }}>Latest camera thumbnail is unavailable</div>
          )}
          {config.regions.map((region, index) => (
            <div
              key={region.id}
              title={`${region.name}: ${region.sensitivity}% sensitivity`}
              style={{ position: "absolute", left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.width * 100}%`, height: `${region.height * 100}%`, border: `2px solid ${region.enabled ? "#a855f7" : "#94a3b8"}`, background: region.enabled ? "rgba(168,85,247,.18)" : "rgba(100,116,139,.14)", boxSizing: "border-box", pointerEvents: "none" }}
            >
              <span style={{ position: "absolute", top: 4, left: 4, padding: "2px 6px", borderRadius: 6, color: "#fff", background: "rgba(76,29,149,.88)", fontSize: 10, fontWeight: 900 }}>{index + 1} · {region.sensitivity}%</span>
            </div>
          ))}
          {draft && <div style={{ position: "absolute", left: `${draft.x * 100}%`, top: `${draft.y * 100}%`, width: `${draft.width * 100}%`, height: `${draft.height * 100}%`, border: "2px dashed #7c3aed", background: "rgba(124,58,237,.16)", boxSizing: "border-box", pointerEvents: "none" }} />}
        </div>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ color: "#0f172a", fontSize: 13 }}>Motion regions</strong>
          <span style={{ color: "#64748b", fontSize: 11 }}>Sensitivity can be adjusted per region unless the camera reports single-sensitivity mode.</span>
        </div>
        {config.regions.length === 0 ? (
          <div style={{ padding: 18, border: "1px dashed #cbd5e1", borderRadius: 12, color: "#64748b", textAlign: "center", fontSize: 12 }}>No motion regions configured.</div>
        ) : config.regions.map((region, index) => (
          <article key={region.id} style={{ display: "grid", gridTemplateColumns: "auto minmax(150px, 1fr) minmax(210px, 1.2fr) auto", gap: 12, alignItems: "center", padding: 12, border: "1px solid #e2e8f0", borderRadius: 12, background: "#f8fafc" }}>
            <label style={{ display: "grid", justifyItems: "center", gap: 3, color: "#64748b", fontSize: 10, fontWeight: 800 }}>
              <input type="checkbox" checked={region.enabled} disabled={saving} onChange={(event) => updateRegion(region.id, { enabled: event.target.checked })} /> Active
            </label>
            <input aria-label={`Motion region ${index + 1} name`} value={region.name} maxLength={80} disabled={saving} onChange={(event) => updateRegion(region.id, { name: event.target.value })} style={{ minHeight: 38, padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 9, font: "inherit", fontSize: 12, fontWeight: 700 }} />
            <label style={{ display: "grid", gap: 5, color: "#475569", fontSize: 11, fontWeight: 700 }}>
              Sensitivity {region.sensitivity}%
              <input type="range" min={0} max={100} value={region.sensitivity} disabled={saving || capabilities?.singleSensitivitySupport === true} onChange={(event) => updateRegion(region.id, { sensitivity: Number(event.target.value) })} />
            </label>
            <button type="button" disabled={saving} onClick={() => deleteRegion(region.id)} style={{ ...buttonStyle, color: "#b91c1c", borderColor: "#fecaca", background: "#fff7f7" }}>Delete</button>
          </article>
        ))}
      </div>

      {message && <div role={error ? "alert" : "status"} style={{ padding: "10px 12px", border: `1px solid ${error ? "#fecaca" : "#bbf7d0"}`, borderRadius: 10, color: error ? "#b91c1c" : "#166534", background: error ? "#fef2f2" : "#f0fdf4", fontSize: 12, fontWeight: 700 }}>{message}</div>}

      <footer style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" disabled={!isDirty || saving} onClick={() => { setConfig(saved); setMessage(""); setError(false); }} style={{ ...buttonStyle, opacity: !isDirty || saving ? 0.5 : 1 }}>Reset</button>
        <button type="button" disabled={!isDirty || saving} onClick={() => void saveDesiredSettings()} style={{ ...buttonStyle, borderColor: "#7c3aed", color: "#fff", background: "#7c3aed", opacity: !isDirty || saving ? 0.55 : 1 }}>{saving ? "Saving..." : "Save desired motion settings"}</button>
      </footer>
    </section>
  );
}
